// Function index:
//   addDoorwayMarker(loc, layer, project?, liftPx?) — place a small
//       kind-coloured marker on the doorway hex. Every location renders at
//       full strength: unmapped interiors are enterable too (the server
//       generates them JIT on first entry).
//   LOCATION_COLOUR / LOCATION_LETTER — per-kind palette + label.
//
// NM-D1 marker style. Each marker is a filled circle (about half a
// token's diameter) with a single-letter label inscribed. Markers sit on
// the upper edge of the hex so the character token (which centres on the
// hex) doesn't overlap them. Locations are bounded (~30-60 per 100x100
// world) so we don't cull them.
//
// `project` + `liftPx` default to the flat pointy-top HexTile projection;
// hex-scene.ts passes iso/project.ts's isoProject + an iso-scaled lift
// under the iso map style so markers land on the projected tile, not the flat
// pixel grid.

import { Container, Graphics, Text } from 'pixi.js';
import { HEX_SIZE, HexTile } from './palette.js';
import type { LocationInput } from './types.js';

function flatProject(q: number, r: number): { x: number; y: number } {
  const hex = new HexTile({ q, r });
  return { x: hex.x, y: hex.y };
}

export function addDoorwayMarker(
  loc: LocationInput,
  layer: Container,
  project: (q: number, r: number) => { x: number; y: number } = flatProject,
  liftPx: number = HEX_SIZE * 0.45,
): void {
  const { x, y } = project(loc.q, loc.r);
  const container = new Container();
  const colour = LOCATION_COLOUR[loc.kind] ?? 0x888888;
  // Full strength for every doorway — the old 0.4-alpha "don't think you can
  // enter" treatment for NULL interiors described a limitation that no longer
  // exists (JIT generation runs on first entry).
  const alpha = 1;

  const g = new Graphics();
  g.circle(0, 0, HEX_SIZE * 0.20)
    .fill({ color: colour, alpha })
    .stroke({ color: 0x000, width: 1.5, alpha });
  container.addChild(g);

  const letter = (LOCATION_LETTER[loc.kind] ?? '?');
  const label = new Text({
    text: letter,
    style: { fill: 0x000, fontSize: 9, fontFamily: 'system-ui', fontWeight: 'bold' },
  });
  label.anchor.set(0.5);
  label.alpha = alpha;
  container.addChild(label);

  container.x = x;
  container.y = y - liftPx;
  layer.addChild(container);
}

const LOCATION_COLOUR: Record<string, number> = {
  cave:      0x4a4a4a,
  tower:     0x88c0ff,
  labyrinth: 0xa888ff,
  dungeon:   0xc04848,
  city:      0xf5c468,
  town:      0xe0a848,
  village:   0xc88838,
  hamlet:    0xa87028,
  castle:    0xc8c8d0,
  monastery: 0xddddc8,
  ruin:      0x886848,
};
const LOCATION_LETTER: Record<string, string> = {
  cave:      'C',
  tower:     'T',
  labyrinth: 'L',
  dungeon:   'D',
  city:      'C',
  town:      'T',
  village:   'V',
  hamlet:    'H',
  castle:    'K',  // K for Keep, since C is taken
  monastery: 'M',
  ruin:      'R',
};
